import React, { useRef, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import useDataLayer from 'src/hooks/useDataLayer';
import usePrice from 'src/hooks/usePrice';
import usePrevious from 'src/hooks/usePrevious';

import 'objectFitPolyfill';

import { openModal, previewAccessory } from 'src/redux/configurator/actions';

import CheckIcon from 'src/assets/icons/check.svg';
import EyeSlashIcon from 'src/assets/icons/eye-slash.svg';
import Wrench from 'src/assets/icons/wrench.svg';
import InfoCircle from 'src/assets/icons/info-circle.svg';
import PlusIcon from 'src/assets/icons/plus.svg';
import { getViewportHeight, getViewportWidth, formatFittingTime, truncateString } from 'helpers/utilities';
import theme from 'src/lib/theme';
import Label from 'components/_utility/Label';
import {
  Card,
  ImageWrap,
  Image,
  InfoWrap,
  Info,
  Name,
  PartNumber,
  PartNumberWrap,
  TotalWrap,
  Price,
  Fitting,
  Triangle,
  SelectedIndicator,
  NotVisible,
  InfoCircleWrap,
  AddIconWrap,
  TriumphTriangle,
  DetailsButton,
} from './AccessoryCard.styled';
import cloudinary from 'helpers/cloudinary';

const AccessoryCard = ({
  id,
  name,
  partNumber,
  price,
  priceLocalised,
  images = [],
  selected,
  onSelectCard,
  bikeImages = [],
  fittingTime,
  cardIndex,
  horizontalScroll,
  scrollX,
  scrollY,
  onSelectEye,
  ...accessory
}) => {
  const dispatch = useDispatch();
  const formatPrice = usePrice();
  const pushToDataLayer = useDataLayer();

  const image = Array.isArray(images) && images[0];
  const notVisible = !bikeImages.length;
  const [imageLoaded, setImageLoaded] = useState(false);
  const cardRef = useRef();
  const imageRef = useRef();
  const prevScrollX = usePrevious(scrollX);
  const prevScrollY = usePrevious(scrollY);

  const nameLength = getViewportWidth() <= theme.breakpoints.tabletLarge ? 30 : 50;

  useEffect(() => {
    const cardEl = cardRef.current;
    const imageEl = imageRef.current;

    if (!image || !image.src || !cardEl || !imageEl) {
      return;
    }

    setTimeout(() => {
      const loadScrollTopThreshold = 400;
      const viewportHeight = getViewportHeight();
      const viewportWidth = getViewportWidth();
      const cardVisibleY =
        (!horizontalScroll || viewportWidth >= theme.breakpoints.desktop) &&
        cardEl.getBoundingClientRect().top - loadScrollTopThreshold <= viewportHeight;
      const cardVisibleX =
        horizontalScroll &&
        viewportWidth < theme.breakpoints.desktop &&
        cardEl.getBoundingClientRect().left - loadScrollTopThreshold <= viewportWidth;

      if (cardVisibleY || cardVisibleX || cardIndex < 6) {
        const imageObj = new window.Image();
        imageObj.src = cloudinary(window.decodeURI(image.src), 'c_thumb,w_252');

        imageObj.onload = () => {
          imageEl.src = imageObj.src;
          setImageLoaded(true);
        };
      }
    });
  }, [cardIndex, horizontalScroll, image, name, partNumber, prevScrollX, prevScrollY, scrollX, scrollY]);

  useEffect(() => {
    window.objectFitPolyfill();
  }, []);

  return (
    <Card ref={cardRef} onClick={() => onSelectCard(id)} data-testid="accessory" horizontalScroll={horizontalScroll}>
      <ImageWrap>
        {image ? <Image ref={imageRef} alt={image.alt} loaded={imageLoaded} data-object-fit /> : <TriumphTriangle />}
        {notVisible ? (
          <NotVisible onClick={onSelectEye}>
            <EyeSlashIcon />
          </NotVisible>
        ) : null}
        <AddIconWrap>
          <PlusIcon />
        </AddIconWrap>
      </ImageWrap>
      <InfoWrap>
        <Info>
          <Name>{truncateString(name, nameLength)}</Name>
          <PartNumberWrap>
            <Label name="partNumberLabel" />
            <PartNumber>{partNumber}</PartNumber>
          </PartNumberWrap>
        </Info>
        <TotalWrap>
          <Price>{price ? formatPrice(price) : priceLocalised}</Price>
          <Wrench></Wrench>
          <Fitting>{formatFittingTime(fittingTime)}</Fitting>
        </TotalWrap>
      </InfoWrap>
      <DetailsButton
        onClick={event => [
          event.stopPropagation(),
          pushToDataLayer('configuratorAccessoryInfo', {
            configuratorAccessoryName: name,
            configuratorAccessoryID: partNumber,
            configuratorAccessoryPrice: price,
          }),
          dispatch(openModal('accessories')),
          dispatch(
            previewAccessory({
              id,
              name,
              price,
              priceLocalised,
              images,
              selected,
              onSelectCard,
              bikeImages,
              fittingTime,
              partNumber,
              ...accessory,
            })
          ),
        ]}
      >
        <InfoCircleWrap data-testid="infoCircle">
          <InfoCircle />
        </InfoCircleWrap>
        <span>
          <Label name="detailsButton" />
        </span>
      </DetailsButton>
      {selected ? (
        <SelectedIndicator>
          <Triangle />
          <CheckIcon />
        </SelectedIndicator>
      ) : null}
    </Card>
  );
};
export default AccessoryCard;
